import { useCallback, useEffect, useState } from "react";

import { WL_REASON_ELI_SUICIDE } from "utils/eligibility";
import {
  CLINICAL_INELIGIBLE,
  CLINICAL_PROGRAM,
  CORE_PRODUCT,
  DIABETES_PROGRAM,
  DIABETES_WORKSHOP_PROGRAM,
  GLP1_PROGRAM,
  GLP1_WORKSHOP_PROGRAM,
  PREMIUM_PRODUCT,
  resultToEventMap
} from "utils/resultFunctions/constants";
import { useSubmitAssessment } from "utils/assessment";
import { hasAnsweredAllQuestions } from "utils/branching";
import { dispatchEvent, pageView, setPageData } from "utils/analytics";

import useAppContext from "components/AppContext";
import RedirectFirstQuestion from "components/RedirectFirstQuestion";
import Error from "components/Results/Error";
import ResultsEliNone from "components/Results/ResultsEliNone";
import ResultsEliSuicide from "components/Results/ResultsEliSuicide";
import { ResultsUni5Default } from "./ResultsUni5Default";
import { ResultsUni5Clinical } from "./ResultsUni5Clinical";
import { saveUni5ResultsData } from "components/Results/storeResultsData";
import useUrlSlugContext from "components/UrlSlugContext";
import { ResultsEmail } from "./ResultsEmail";

export const ResultsUni5: React.FC = () => {
  const { baseUrl } = useUrlSlugContext();
  const [blur, setBlur] = useState(true);
  const { newQuiz, profile, answers, nextStack, contact, assessment } =
    useAppContext();
  const { market } = contact || {};
  const {
    assessmentId,
    submit,
    isSubmitting,
    isSuccess,
    isFailure,
    resultsScreen
  } = useSubmitAssessment();
  const isAssessmentComplete = hasAnsweredAllQuestions(answers, nextStack);

  const handleSubmit = useCallback(() => {
    if (!isAssessmentComplete) {
      return;
    }

    submit({
      answers,
      newQuiz,
      questions: assessment?.questions,
      profile,
      market
    });
  }, [isAssessmentComplete]);

  useEffect(() => {
    pageView("result");

    dispatchEvent({
      event: "non_click_event",
      event_key: "quiz:ended",
      event_category: "quiz",
      event_action: "quiz_ended"
    });
  }, []);

  useEffect(() => {
    handleSubmit();
  }, [handleSubmit]);

  useEffect(() => {
    if (!isAssessmentComplete || !isSuccess) {
      return;
    }

    setPageData({
      assessment_result: resultToEventMap[resultsScreen] || CLINICAL_INELIGIBLE
    });

    // If this is a waitlist screen, save results data immediately
    // because these do not have email capture modals.
    if (
      ![
        CORE_PRODUCT,
        PREMIUM_PRODUCT,
        DIABETES_PROGRAM,
        DIABETES_WORKSHOP_PROGRAM,
        GLP1_PROGRAM,
        GLP1_WORKSHOP_PROGRAM,
        CLINICAL_PROGRAM
      ].includes(resultsScreen)
    ) {
      saveUni5ResultsData({
        assessmentId,
        answers,
        assessment,
        baseUrl,
        resultsScreen,
        profile
      });
    }
  }, [
    resultsScreen,
    assessmentId,
    answers,
    assessment,
    baseUrl,
    profile,
    isAssessmentComplete,
    isSuccess
  ]);

  const onEmailComplete = () => {
    setBlur(false);

    saveUni5ResultsData({
      assessmentId,
      answers,
      assessment,
      baseUrl,
      resultsScreen,
      profile
    });

    dispatchEvent({
      event: "non_click_event",
      event_key: "quiz:email_capture_submitted",
      event_category: "quiz",
      event_action: "email_capture_submitted"
    });
  };

  if (!isAssessmentComplete) {
    return <RedirectFirstQuestion />;
  }

  if (isFailure) {
    return <Error retry={handleSubmit} />;
  }

  if (isSubmitting) {
    return <></>;
  }

  switch (resultsScreen) {
    case CORE_PRODUCT:
    case PREMIUM_PRODUCT:
    case DIABETES_PROGRAM:
    case DIABETES_WORKSHOP_PROGRAM:
    case GLP1_PROGRAM:
    case GLP1_WORKSHOP_PROGRAM:
      return (
        <>
          <ResultsEmail
            onComplete={onEmailComplete}
            resultsScreen={resultsScreen}
          />
          <ResultsUni5Default resultsScreen={resultsScreen} blur={blur} />
        </>
      );

    case CLINICAL_PROGRAM:
      return (
        <>
          <ResultsEmail
            onComplete={onEmailComplete}
            resultsScreen={resultsScreen}
          />
          <ResultsUni5Clinical blur={blur} />
        </>
      );

    case WL_REASON_ELI_SUICIDE:
      return <ResultsEliSuicide />;

    default:
      return <ResultsEliNone />;
  }
};
